/*
    Hook que decta cuál seccion de la página está viendo el usuario en la pantalla mientas hace scroll, muy 
    común para iluminar o marcar el botón activo en un menú de navegación (scrollspy).

    Puntos clave:
        Usa IntersectionObserver: Monitorea en tiempo real qué secciones (sectionIds) están visibles en el 
        viewport de forma eficiente sin recargar el navegador.

        Determina la sección principal: Si hay varias secciones visibles a la vez, calcula su posición 
        (getBoundingClientRect) y selecciona la que está más arriba.

        Ajuste fino (rootMargin / offsetTop): Permite compensar barras de navegación fijas (headers sticky) 
        para que no tapen ni desfasen la detección.

        Reactivo al DOM (MutationObserver): Si el contenido cambia dinámicamente (por ejemplo, si se carga 
        un elemento nuevo o desaparece una sección), el hook vuelve a enganchar las secciones automáticamente 
        sin romper el rastreo.
*/


import { useState, useEffect, useRef } from "react";


export const useActiveSection = (sectionIds = [], { offsetTop = 0, deps = [] } = {}) => {

    const REF__useRef__visibleIds = useRef(new Set());
    const [useActiveSectionId, setActiveSectionId] = useState(null);

    useEffect(() => {
        if (!Array.isArray(sectionIds) || sectionIds.length === 0) return;
        setActiveSectionId(null);
        REF__useRef__visibleIds.current = new Set();

        let IO = null;
        let OBSERVED = [];

        const recomputeActive = () => {
            const VISIBLE = [...REF__useRef__visibleIds.current].map((id) => {
                const EL = document.getElementById(id);
                return EL ? { id, top: EL.getBoundingClientRect().top } : null;
            }).filter(Boolean).sort((a, b) => a.top - b.top);

            setActiveSectionId(VISIBLE.length > 0 ? VISIBLE[0].id : null);
        };

        const attach = () => {
            const SECTIONS = sectionIds.map((id) => document.getElementById(id)).filter(Boolean);

            const SAME = SECTIONS.length === OBSERVED.length && SECTIONS.every((el, i) => el === OBSERVED[i]);
            if (SAME) return;

            if (IO) IO.disconnect();
            REF__useRef__visibleIds.current = new Set();

            IO = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            REF__useRef__visibleIds.current.add(entry.target.id);
                        } else {
                            REF__useRef__visibleIds.current.delete(entry.target.id);
                        }
                    });
                    recomputeActive();
                },{ rootMargin: `-${offsetTop + 20}px 0px -55% 0px`, threshold: 0 }
            );

            SECTIONS.forEach((s) => IO.observe(s));
            OBSERVED = SECTIONS;
            recomputeActive();
        };

        attach();

        const MO = new MutationObserver(attach);
        MO.observe(document.body, { childList: true, subtree: true });

        const onScroll = () => recomputeActive();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);

        return () => {
            if (IO) IO.disconnect();
            MO.disconnect();
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, [sectionIds.join(","), offsetTop, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

    return useActiveSectionId;
};